import React, { useState, PropsWithChildren } from 'react';
import { useMutation } from '@apollo/client/react/hooks';
import Box from '@mui/material/Box';
import FormGroup from '@mui/material/FormGroup';
import { Field, FieldProps, Formik } from 'formik';
import { gql } from '@yi/core';
import { i18n_create_an_account } from '@yi/i18n/phrases/i18n_create_an_account';
import { i18n_email_address } from '@yi/i18n/phrases/i18n_email_address';
import { i18n_forgot_your_password } from '@yi/i18n/phrases/i18n_forgot_your_password';
import { i18n_password } from '@yi/i18n/phrases/i18n_password';
import { i18n_sign_in } from '@yi/i18n/phrases/i18n_sign_in';

import { analytics, isNonEmptyString, useStorage, utilities } from '~/common';
import { useMaybeRedirectAfterPasswordReset } from '~/src/common/sign-in';
import { CircularProgress } from '~/src/components/buttons/button-styles';
import { FormErrorWrapper } from '~/src/components/FormErrorWrapper';
import { FormInput } from '~/src/components/FormInput';
import { useLocale } from '~/src/components/LocaleProvider';
import { LocalizedLink } from '~/src/components/LocalizedLink';
import { PasswordVisibilityIcon } from '~/src/components/PasswordVisibilityIcon';

import { SignInForgotPassword, SignInFormDivider, SignInMain, SignInWrapper } from './elements';
import { Heading, Button } from '../elements';

interface FormikSignInFormValues {
  username?: string;
  password?: string;
}

interface Props {
  showPaywall?: boolean;
  memberType?: string;
  heading?: string;
  email?: string;
  onClickForgotPassword?(): void;
  setEmail(event: string): void;
  closeModal(): void;
  hideCreateAccount?: boolean;
  wrapperStyles?: React.CSSProperties;
  mainStyles?: React.CSSProperties;
  onSignInError?: (e: Error | string) => void;
}

export const SignInForm: React.FC<PropsWithChildren<Props>> = ({
  heading,
  email,
  onClickForgotPassword,
  setEmail,
  closeModal,
  hideCreateAccount,
  wrapperStyles,
  mainStyles,
  onSignInError = () => undefined,
}) => {
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const { locale } = useLocale();
  const storage = useStorage();

  const hasEmailPrefilled = isNonEmptyString(email);
  const { maybeRedirect } = useMaybeRedirectAfterPasswordReset();

  const [login] = useMutation<
    gql.typesClient.Mutate_Login_Base,
    gql.typesClient.Mutate_Login_BaseVariables
  >(gql.Mutate_Login_Base, {
    update: async (store, { data }) => {
      if (!data?.login?.token) {
        return console.warn('Login failed, no token received');
      }
      const { token, current_user } = data.login;
      storage.setToken(token);
      store.writeQuery({ query: gql.Query_CurrentUser_Base, data: { CurrentUser: current_user } });
      // @TODO write `user` field data from `loginResponse` to cache to
      // prevent need of second request for CurrentUser data, if possible
      //
      // if (data && data.login && data.login.user) {
      //   client.writeQuery({
      //     query: CURRENT_USER,
      //     data: {
      //       CurrentUser: { ...data.login.user, },
      //     },
      //   });
      // }
    },
    onError: error => {
      // If graphQL error, onError is necessary for mutation promise to resolve
      console.warn(error);
      onSignInError(error);
    },
  });
  const defaultPromo = utilities.getLocalizedDefaultMemPromo(locale);
  return (
    <SignInWrapper id="sign-up-form-wrapper" style={wrapperStyles}>
      <Heading style={{ textAlign: 'center', marginTop: '0' }}>
        {heading || i18n_sign_in(locale)}
      </Heading>
      <Formik
        initialValues={{
          username: email ? email : '',
          password: '',
        }}
        onSubmit={async (values, { setSubmitting, setStatus }) => {
          const variables: gql.typesClient.Mutate_Login_BaseVariables = {
            username: values.username,
            password: values.password,
          };
          const res = await login({ variables });
          setSubmitting(false);
          // handle mutation error
          const errors = res?.data?.login ? res.data.login.errors : null;
          if (errors) {
            setStatus({ errors });
            onSignInError(errors.join(';;'));
          } else if (!res) {
            // handle graphQL error
            setStatus({ errors: 'Server Error' });
            onSignInError('Server Error');
          } else {
            window.scrollTo(0, 0);
            closeModal();
            analytics.sendLoginEvent();
            maybeRedirect();
          }
        }}
      >
        {({ handleSubmit, isSubmitting, setStatus, status }) => (
          <form data-testid="sign-in-form" onSubmit={handleSubmit} style={{ width: '100%' }}>
            <SignInMain style={mainStyles}>
              <FormGroup>
                <Field name="username">
                  {({ field }: FieldProps<FormikSignInFormValues>) => (
                    <FormInput
                      {...field}
                      {...(!hasEmailPrefilled && { autoFocus: true })}
                      label={i18n_email_address(locale)}
                      type="email"
                      id="modalInputEmail"
                      data-testid="signin-modal-input-email"
                      name="username"
                      inputProps={{
                        autoComplete: 'email',
                      }}
                      data-required="true"
                      onInput={(e: React.ChangeEvent<HTMLInputElement>) => {
                        setEmail(e.target.value);
                        if (status?.errors) setStatus({});
                      }}
                      placeholder={i18n_email_address(locale)}
                      fullWidth
                      variant="filled"
                    />
                  )}
                </Field>
                <Field name="password">
                  {({ field }: FieldProps<FormikSignInFormValues>) => (
                    <FormInput
                      {...field}
                      onInput={() => {
                        if (status?.errors) setStatus({});
                      }}
                      {...(hasEmailPrefilled && { autoFocus: true })}
                      label={i18n_password(locale)}
                      type={isPasswordVisible ? 'text' : 'password'}
                      id="modalInputPassword"
                      data-testid="signin-modal-input-password"
                      name="password"
                      placeholder={i18n_password(locale)}
                      fullWidth
                      variant="filled"
                      inputProps={{
                        autoComplete: 'current-password',
                      }}
                      InputProps={{
                        endAdornment: <PasswordVisibilityIcon onChange={setIsPasswordVisible} />,
                      }}
                    />
                  )}
                </Field>
                <input type="hidden" name="auto_login" value="1" />
                <FormErrorWrapper data-testid="sign-in-error-label">
                  {status?.errors}
                </FormErrorWrapper>
                <Button
                  type="submit"
                  disabled={isSubmitting}
                  data-testid="signin-modal-submit-button"
                >
                  {isSubmitting && <CircularProgress spinnerColor="white" size={24} />}
                  {i18n_sign_in(locale)}
                </Button>
                <SignInForgotPassword
                  href="javascript:void(0)"
                  onClick={onClickForgotPassword}
                  aria-label="Opens Sign In Modal"
                  data-target="ygi-forgot-password-modal"
                >
                  {i18n_forgot_your_password(locale)}
                </SignInForgotPassword>
              </FormGroup>
            </SignInMain>
            {!hideCreateAccount && (
              <>
                <SignInFormDivider />
                <Box mt={3}>
                  <LocalizedLink to={`/create-account/${defaultPromo}`}>
                    {i18n_create_an_account(locale)}
                  </LocalizedLink>
                </Box>
              </>
            )}
          </form>
        )}
      </Formik>
    </SignInWrapper>
  );
};
